import React, { useState, useEffect, useCallback } from 'react';
import { format, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Trash2, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';

const fmtDt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : format(d, 'dd/MM/yyyy HH:mm', { locale: es });
};

const fmtEuros = (cents) => `${((cents || 0) / 100).toFixed(2)} €`;

const DaysLeftBadge = ({ purgeAt }) => {
  if (!purgeAt) return null;
  const days = differenceInDays(new Date(purgeAt), new Date());
  if (days <= 0) {
    return <Badge className="bg-red-500/20 text-red-400">Borrado inminente</Badge>;
  }
  if (days <= 7) {
    return <Badge className="bg-amber-500/20 text-amber-400">Borrado en {days} día{days === 1 ? '' : 's'}</Badge>;
  }
  return <Badge className="bg-zinc-700/40 text-zinc-300">{days} días restantes</Badge>;
};

export const DeletedReservations = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // id currently being acted on

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listDeletedReservations(200);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Error al cargar reservas eliminadas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleRestore = async (id, code) => {
    if (!window.confirm(`Restaurar la reserva ${code || ''}? Volverá a la lista de reservas activas.`)) return;
    setActing(id);
    try {
      await adminApi.restoreDeletedReservation(id);
      toast.success('Reserva restaurada');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al restaurar');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="deleted-reservations-section">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Reservas Eliminadas</h2>
          <p className="text-zinc-500 text-sm mt-1 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Las reservas eliminadas se conservan aquí <span className="text-zinc-300">30 días</span>. Después se borran automáticamente.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchItems}
          disabled={loading}
          data-testid="refresh-deleted-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
        </Button>
      </div>

      {loading ? (
        <div className="rounded-xl border border-white/5 bg-zinc-900/50 p-12 text-center text-zinc-500">
          Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-white/5 bg-zinc-900/50 p-12 text-center" data-testid="deleted-empty-state">
          <Trash2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400">No hay reservas eliminadas</p>
        </div>
      ) : (
        <div className="rounded-xl border border-white/5 bg-zinc-900/30 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/70 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Código</th>
                <th className="text-left p-4">Cliente</th>
                <th className="text-left p-4">Matrícula</th>
                <th className="text-left p-4">Periodo</th>
                <th className="text-right p-4">Total</th>
                <th className="text-left p-4">Eliminada</th>
                <th className="text-left p-4">Por</th>
                <th className="text-left p-4">Auto-borrado</th>
                <th className="text-right p-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`deleted-row-${it.reservation_code || it.id}`}>
                  <td className="p-4 font-mono text-white">{it.reservation_code || '—'}</td>
                  <td className="p-4 text-zinc-300">
                    <div>{it.customer_name || '—'}</div>
                    <div className="text-xs text-zinc-500">{it.customer_email || ''}</div>
                  </td>
                  <td className="p-4 text-zinc-300 font-mono">{it.vehicle_plate || '—'}</td>
                  <td className="p-4 text-zinc-400 text-xs">
                    <div>{fmtDt(it.start_dt)}</div>
                    <div className="text-zinc-500">→ {fmtDt(it.end_dt)}</div>
                  </td>
                  <td className="p-4 text-right text-white font-semibold">{fmtEuros(it.total_cents)}</td>
                  <td className="p-4 text-zinc-400 text-xs">{fmtDt(it.deleted_at)}</td>
                  <td className="p-4 text-zinc-400 text-xs">{it.deleted_by || '—'}</td>
                  <td className="p-4"><DaysLeftBadge purgeAt={it.purge_at} /></td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={acting === it.id}
                        onClick={() => handleRestore(it.id, it.reservation_code)}
                        data-testid={`restore-${it.reservation_code || it.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Restaurar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DeletedReservations;
